import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { apiV2 } from '@/lib/api-v2-client'
import { toast } from 'sonner'
import { SiteFooter } from '@/components/site-footer'
import { CheckCircle, XCircle, Loader2, Smartphone } from 'lucide-react'
import { afterSignInPath, isSafeRedirect } from '@/lib/post-auth-redirect'
import { TwoFactorCodeForm } from '@/components/two-factor-code-form'

const needsTotp = (res: unknown) =>
  typeof res === 'object' && res !== null && (res as { totp_required?: boolean }).totp_required === true

export const Route = createFileRoute('/auth/verify-magic-link')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || '',
    email: (search.email as string) || '',
    redirect: isSafeRedirect(search.redirect) ? search.redirect : undefined,
    // Set by the server on links that the mobile app asked for. iOS opens such a
    // link in the app (universal link); when it opens here instead, the token must
    // stay unused so the app can still sign in with it.
    client: search.client === 'mobile' ? ('mobile' as const) : undefined,
  }),
  component: VerifyMagicLinkPage,
  head: () => ({
    meta: [{ title: 'Signing in — Uptrack' }],
  }),
})

function redirectAfterSignIn(redirect: string | undefined) {
  toast.success('Signed in!')
  // The emailed link's redirect wins (works across devices), then the
  // same-browser localStorage copy (a long OAuth consent URL), then the dashboard.
  const dest = afterSignInPath(redirect)
  // HARD navigation (full page load), NOT the SPA router. The verify
  // response set the `_uptrack_key` session cookie, but on a cold load
  // (clicking the email link) the auth provider already ran its initial,
  // cookie-less restoreSession() → the context is logged-out, and a
  // client-side navigate() to /dashboard hits the _authenticated guard
  // before the new session is reflected → it bounces back to /login
  // ("you're in" but stuck on login). A full reload re-boots the app with
  // the cookie present so restoreSession() authenticates — same mechanism
  // that makes the OAuth (Google/GitHub) callback work.
  setTimeout(() => { window.location.href = dest }, 800)
}

function VerifyMagicLinkPage() {
  const { token, email, redirect, client } = Route.useSearch()
  const [status, setStatus] = useState<'mobile' | 'verifying' | 'totp' | 'success' | 'error'>(
    client === 'mobile' && token && email ? 'mobile' : 'verifying',
  )
  const [errorMessage, setErrorMessage] = useState('')
  // The free plan limits new accounts per network per day. The link is spent, so the
  // title must not say "expired": the user needs a new link tomorrow, not a retry now.
  const [signupLimited, setSignupLimited] = useState(false)
  const [copied, setCopied] = useState(false)

  // The fallback never hands the token to a custom URL scheme (any app can claim
  // `uptrack://`). The user copies this https link and pastes it into the app's
  // "Sign-in link or code" field, which accepts the whole link.
  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
    } catch {
      toast.error('Could not copy. Copy the address of this page instead.')
    }
  }

  function verify() {
    setStatus('verifying')
    apiV2.verifyMagicLink(email, token)
      .then((res) => {
        if (needsTotp(res)) {
          setStatus('totp')
          return
        }
        setStatus('success')
        redirectAfterSignIn(redirect)
      })
      .catch((err) => {
        setStatus('error')
        setSignupLimited(err?.code === 'signup_rate_limited')
        setErrorMessage(err?.message || 'This link is invalid or has expired.')
      })
  }

  useEffect(() => {
    if (!token || !email) {
      setStatus('error')
      setErrorMessage('Invalid link. Please request a new magic link.')
      return
    }
    if (client === 'mobile') return
    verify()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, email, redirect, client])

  async function submitTotp(code: string) {
    try {
      const res = await apiV2.verifyMagicLink(email, token, code)
      if (needsTotp(res)) {
        toast.error('Enter your authentication code.')
        return
      }
      setStatus('success')
        redirectAfterSignIn(redirect)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid 2FA code')
    }
  }


  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {status === 'mobile' && (
              <>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                  <Smartphone className="h-7 w-7 text-primary" />
                </div>
                <CardTitle className="text-2xl">Open the Uptrack app</CardTitle>
                <CardDescription>
                  You asked for this sign-in link in the Uptrack app. Copy the link, open the app, and paste it into
                  "Sign-in link or code".
                </CardDescription>
              </>
            )}
            {status === 'verifying' && (
              <>
                <div className="mx-auto mb-4">
                  <Loader2 className="h-10 w-10 animate-spin text-primary" />
                </div>
                <CardTitle className="text-2xl">Signing you in...</CardTitle>
                <CardDescription>Verifying your magic link</CardDescription>
              </>
            )}
            {status === 'totp' && (
              <>
                <CardTitle className="text-2xl">Two-factor authentication</CardTitle>
                <CardDescription>Enter the code from your authenticator app, or a backup code.</CardDescription>
              </>
            )}
            {status === 'success' && (
              <>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-status-up-soft">
                  <CheckCircle className="h-7 w-7 text-status-up" />
                </div>
                <CardTitle className="text-2xl">You're in!</CardTitle>
                <CardDescription>{redirect?.startsWith('/checkout') ? 'Taking you to checkout...' : 'Redirecting to your dashboard...'}</CardDescription>
              </>
            )}
            {status === 'error' && (
              <>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-status-down-soft">
                  <XCircle className="h-7 w-7 text-status-down" />
                </div>
                <CardTitle className="text-2xl">
                  {signupLimited ? 'Sign-up limit reached' : 'Link expired or invalid'}
                </CardTitle>
                <CardDescription>{errorMessage}</CardDescription>
              </>
            )}
          </CardHeader>
          {status === 'mobile' && (
            <CardContent className="flex flex-col gap-3 text-center">
              <Button className="w-full" onClick={copyLink}>
                {copied ? 'Link copied' : 'Copy sign-in link'}
              </Button>
              <Button variant="ghost" className="w-full" onClick={verify}>
                Sign in on this browser instead
              </Button>
            </CardContent>
          )}
          {status === 'totp' && (
            <CardContent>
              <TwoFactorCodeForm onSubmit={submitTotp} />
            </CardContent>
          )}
          {status === 'error' && (
            <CardContent className="text-center">
              <Button asChild className="w-full">
                <a href={redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login'}>Request a new link</a>
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
      <SiteFooter />
    </div>
  )
}
